import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ArrowRight, ArrowDown, Compass, Mountain, Users, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import CircuitCard from "@/components/CircuitCard";
import TestimonialsCarousel from "@/components/TestimonialsCarousel";
import UpcomingDepartures from "@/components/UpcomingDepartures";
import HeroCarousel from "@/components/HeroCarousel";
import SEOHead from "@/components/SEOHead";
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from "@/utils/languageRouter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const { lang: urlLang } = useParams();
  const lang = urlLang || i18n.language;
  const currentLang = SUPPORTED_LANGUAGES.includes(lang) ? lang : DEFAULT_LANGUAGE;
  
  const localizedLink = (path) => {
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `/${currentLang}${cleanPath}`;
  };
  
  const [circuits, setCircuits] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [upcomingDepartures, setUpcomingDepartures] = useState([]);
  const [heroSlides, setHeroSlides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [circuitsRes, testimonialsRes, departuresRes, slidesRes] = await Promise.all([
          axios.get(`${API}/circuits`),
          axios.get(`${API}/testimonials`),
          axios.get(`${API}/upcoming-departures?limit=6`),
          axios.get(`${API}/hero-slides`),
        ]);
        setCircuits(circuitsRes.data);
        setTestimonials(testimonialsRes.data);
        setUpcomingDepartures(departuresRes.data);
        setHeroSlides(slidesRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const features = [
    {
      icon: Compass,
      title: { fr: "Circuits 4x4", en: "4x4 Circuits", ar: "جولات 4x4", nl: "4x4 Circuits", es: "Circuitos 4x4" },
      description: { fr: "Explorez le Sahara en véhicule tout-terrain", en: "Explore the Sahara in an all-terrain vehicle", ar: "استكشف الصحراء بسيارة رباعية الدفع", nl: "Verken de Sahara in een terreinwagen", es: "Explora el Sahara en vehículo todoterreno" },
      link: "/circuits/4x4",
    },
    {
      icon: Mountain,
      title: { fr: "Treks à Pied", en: "Hiking Treks", ar: "رحلات المشي", nl: "Wandeltochten", es: "Caminatas" },
      description: { fr: "Marchez au rythme des caravanes", en: "Walk at the pace of caravans", ar: "امشِ بإيقاع القوافل", nl: "Loop op het ritme van karavanen", es: "Camina al ritmo de las caravanas" },
      link: "/circuits/trekking",
    },
    {
      icon: Users,
      title: { fr: "Guides Locaux", en: "Local Guides", ar: "مرشدون محليون", nl: "Lokale Gidsen", es: "Guías Locales" },
      description: { fr: "Équipe sahraouie passionnée", en: "Passionate Saharan team", ar: "فريق صحراوي شغوف", nl: "Gepassioneerd Sahara team", es: "Equipo saharaui apasionado" },
      link: "/team",
    },
    {
      icon: Star,
      title: { fr: "Nuits Étoilées", en: "Starry Nights", ar: "ليالي مرصعة بالنجوم", nl: "Sterrennachten", es: "Noches Estrelladas" },
      description: { fr: "Dormez sous le ciel du désert", en: "Sleep under the desert sky", ar: "نم تحت سماء الصحراء", nl: "Slaap onder de woestijnhemel", es: "Duerme bajo el cielo del desierto" },
      link: "/blog/dormir-sous-les-etoiles-sahara",
    },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEOHead />
      
      {/* Hero Section with Carousel */}
      <section className="relative h-screen group">
        {/* Hero Carousel */}
        <HeroCarousel slides={heroSlides} interval={6000} />

        {/* Hero Content - Always visible on top of carousel */}
        <div className="relative z-10 h-full flex flex-col justify-center items-center text-center px-4 pt-20 pointer-events-none">
          <div className="max-w-4xl animate-fade-in pointer-events-auto">
            <span className="inline-block text-primary font-medium tracking-widest uppercase text-sm mb-4 bg-white/10 backdrop-blur-sm px-4 py-1 rounded-full">
              XploreSahara
            </span>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-tight drop-shadow-lg">
              {t("hero.title")}
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-8 leading-relaxed drop-shadow-md">
              {t("hero.subtitle")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={localizedLink("/circuits")}>
                <Button
                  size="lg"
                  className="rounded-full bg-primary hover:bg-primary/90 text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all"
                  data-testid="hero-cta-btn"
                >
                  {t("hero.cta")}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to={localizedLink("/map")}>
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full border-white text-white hover:bg-white hover:text-secondary text-lg px-8 py-6 backdrop-blur-sm"
                  data-testid="hero-map-btn"
                >
                  {t("nav.map")}
                </Button>
              </Link>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-16 left-1/2 -translate-x-1/2 animate-bounce pointer-events-auto">
            <ArrowDown className="h-8 w-8 text-white/70" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-muted">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Link
                key={index}
                to={localizedLink(feature.link)}
                className="bg-card p-8 rounded-2xl shadow-sm hover:shadow-lg transition-all text-center group cursor-pointer hover:-translate-y-1"
                data-testid={`feature-${index}`}
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary transition-colors">
                  <feature.icon className="h-8 w-8 text-primary group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-serif text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                  {feature.title[lang] || feature.title.fr}
                </h3>
                <p className="text-muted-foreground">
                  {feature.description[lang] || feature.description.fr}
                </p>
                <span className="inline-flex items-center mt-4 text-primary text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  {t("common.learnMore") || "En savoir plus"} →
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Departures Section */}
      {upcomingDepartures.length > 0 && (
        <UpcomingDepartures departures={upcomingDepartures} />
      )}

      {/* Circuits Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-primary font-medium tracking-widest uppercase text-sm">
              {t("circuits.subtitle")}
            </span>
            <h2 className="font-serif text-4xl md:text-5xl font-bold mt-2">
              {t("circuits.title")}
            </h2>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="aspect-[4/3] bg-muted rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {circuits.slice(0, 3).map((circuit) => (
                <CircuitCard key={circuit.id} circuit={circuit} />
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to={localizedLink("/circuits")}>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full"
                data-testid="view-all-circuits-btn"
              >
                {t("circuits.viewDetails")}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Night Section - Testimonials */}
      <section className="py-20 night-section overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-primary font-medium tracking-widest uppercase text-sm">
              {t("testimonials.subtitle")}
            </span>
            <h2 className="font-serif text-4xl md:text-5xl font-bold mt-2 text-white">
              {t("testimonials.title")}
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center">
              <div className="w-full max-w-2xl h-80 bg-white/10 rounded-3xl animate-pulse" />
            </div>
          ) : (
            <TestimonialsCarousel testimonials={testimonials.filter(t => t.is_visible !== false)} />
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-5xl font-bold text-white mb-6">
            {lang === "fr" && "Prêt pour l'aventure ?"}
            {lang === "en" && "Ready for adventure?"}
            {lang === "ar" && "هل أنت مستعد للمغامرة؟"}
            {lang === "nl" && "Klaar voor avontuur?"}
            {lang === "es" && "¿Listo para la aventura?"}
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            {lang === "fr" && "Contactez notre équipe pour organiser votre voyage sur mesure dans le Sahara marocain."}
            {lang === "en" && "Contact our team to organize your tailor-made trip to the Moroccan Sahara."}
            {lang === "ar" && "اتصل بفريقنا لتنظيم رحلتك المصممة خصيصاً في الصحراء المغربية."}
            {lang === "nl" && "Neem contact op met ons team om uw reis op maat naar de Marokkaanse Sahara te organiseren."}
            {lang === "es" && "Contacte con nuestro equipo para organizar su viaje a medida al Sahara marroquí."}
          </p>
          <Link to={localizedLink("/contact")}>
            <Button
              size="lg"
              variant="secondary"
              className="rounded-full text-lg px-8 py-6"
              data-testid="cta-contact-btn"
            >
              {t("nav.contact")}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* SEO Content Section - Departure Cities */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-primary font-medium tracking-widest uppercase text-sm">
              {lang === "fr" ? "Points de départ" : lang === "en" ? "Departure points" : lang === "es" ? "Puntos de salida" : lang === "nl" ? "Vertrekpunten" : "نقاط الانطلاق"}
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold mt-2">
              {lang === "fr" ? "Trek & Circuit 4x4 au départ de" : 
               lang === "en" ? "Trek & 4x4 Tour departing from" :
               lang === "es" ? "Trek y Circuito 4x4 desde" :
               lang === "nl" ? "Trek & 4x4 Tour vertrek vanaf" :
               "رحلة تريك ودائرة 4x4 من"}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { city: "marrakech", name: "Marrakech", desc: lang === "fr" ? "La ville rouge, porte de l'Atlas" : "The red city, gateway to Atlas" },
              { city: "ouarzazate", name: "Ouarzazate", desc: lang === "fr" ? "Hollywood du désert marocain" : "Hollywood of Moroccan desert" },
              { city: "zagora", name: "Zagora", desc: lang === "fr" ? "52 jours vers Tombouctou" : "52 days to Timbuktu" },
              { city: "mhamid", name: "M'Hamid", desc: lang === "fr" ? "Dernière oasis avant le Sahara" : "Last oasis before Sahara" }
            ].map((item) => (
              <Link 
                key={item.city}
                to={localizedLink(`/depart/${item.city}`)}
                className="group bg-card rounded-xl p-6 border hover:border-primary hover:shadow-lg transition-all"
              >
                <h3 className="font-semibold text-xl mb-2 group-hover:text-primary transition-colors">
                  {lang === "fr" ? "Départ" : "From"} {item.name}
                </h3>
                <p className="text-muted-foreground text-sm mb-4">{item.desc}</p>
                <span className="text-primary text-sm font-medium inline-flex items-center gap-1">
                  {lang === "fr" ? "Voir les circuits" : "View tours"}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Rich Text Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="prose prose-lg max-w-none">
            {lang === "fr" ? (
              <>
                <h2 className="font-serif text-3xl font-bold mb-6">Trek Désert Maroc & Circuits 4x4 Sahara avec XploreSahara</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  <strong>XploreSahara</strong>, aussi connu sous le nom de <strong>Sahara Soul Treks</strong> et <strong>SaharaTrek</strong>, 
                  est votre partenaire pour des <strong>treks désert Maroc</strong> authentiques et des <strong>circuits 4x4 Sahara</strong> inoubliables. 
                  Basés à <strong>M'Hamid El Ghizlane</strong>, la porte du désert, nous organisons des voyages vers les plus belles 
                  dunes du Maroc : <strong>Erg Chegaga</strong> et <strong>Erg Zahar</strong>.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Que vous recherchiez une <strong>randonnée désert Maroc</strong> de 3 à 7 jours avec dromadaires, 
                  un <strong>trek nomade</strong> hors des sentiers battus, ou une <strong>retraite spirituelle</strong> au cœur du Sahara, 
                  nos guides locaux berbères vous accompagnent dans une aventure authentique. 
                  <strong>Yoga désert Maroc</strong>, <strong>méditation Sahara</strong>, <strong>bivouac sous les étoiles</strong> : 
                  vivez une expérience transformatrice.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Départs depuis <strong>Marrakech</strong>, <strong>Ouarzazate</strong>, <strong>Zagora</strong> ou directement de <strong>M'Hamid</strong>. 
                  <strong>Trek privé</strong> ou en <strong>petit groupe</strong>, circuit <strong>sur mesure</strong> : 
                  nous adaptons chaque voyage à vos envies pour une immersion totale dans la magie du désert marocain.
                </p>
              </>
            ) : lang === "en" ? (
              <>
                <h2 className="font-serif text-3xl font-bold mb-6">Morocco Desert Trek & Sahara 4x4 Tours with XploreSahara</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  <strong>XploreSahara</strong>, also known as <strong>Sahara Soul Treks</strong> and <strong>SaharaTrek</strong>, 
                  is your partner for authentic <strong>Morocco desert treks</strong> and unforgettable <strong>Sahara 4x4 tours</strong>. 
                  Based in <strong>M'Hamid El Ghizlane</strong>, the gateway to the desert, we organize trips to Morocco's most beautiful 
                  dunes: <strong>Erg Chegaga</strong> and <strong>Erg Zahar</strong>.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Whether you're looking for a <strong>Morocco desert hiking</strong> experience of 3 to 7 days with camels, 
                  an off-the-beaten-path <strong>nomad trek</strong>, or a <strong>spiritual retreat</strong> in the heart of the Sahara, 
                  our local Berber guides accompany you on an authentic adventure. 
                  <strong>Desert yoga Morocco</strong>, <strong>Sahara meditation</strong>, <strong>camping under the stars</strong>: 
                  live a transformative experience.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Departures from <strong>Marrakech</strong>, <strong>Ouarzazate</strong>, <strong>Zagora</strong> or directly from <strong>M'Hamid</strong>. 
                  <strong>Private trek</strong> or <strong>small group</strong>, <strong>custom tour</strong>: 
                  we adapt each trip to your desires for total immersion in the magic of the Moroccan desert.
                </p>
              </>
            ) : lang === "es" ? (
              <>
                <h2 className="font-serif text-3xl font-bold mb-6">Trek Desierto Marruecos y Circuitos 4x4 Sahara con XploreSahara</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  <strong>XploreSahara</strong>, también conocido como <strong>Sahara Soul Treks</strong> y <strong>SaharaTrek</strong>, 
                  es su socio para <strong>treks en el desierto de Marruecos</strong> auténticos y <strong>circuitos 4x4 en el Sahara</strong> inolvidables. 
                  Con base en <strong>M'Hamid El Ghizlane</strong>, la puerta del desierto, organizamos viajes a las dunas más hermosas 
                  de Marruecos: <strong>Erg Chegaga</strong> y <strong>Erg Zahar</strong>.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Salidas desde <strong>Marrakech</strong>, <strong>Ouarzazate</strong>, <strong>Zagora</strong> o directamente desde <strong>M'Hamid</strong>. 
                  <strong>Trek privado</strong> o en <strong>pequeño grupo</strong>, <strong>circuito a medida</strong>: 
                  adaptamos cada viaje a sus deseos para una inmersión total en la magia del desierto marroquí.
                </p>
              </>
            ) : (
              <>
                <h2 className="font-serif text-3xl font-bold mb-6">Woestijn Trek Marokko & Sahara 4x4 Tours met XploreSahara</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  <strong>XploreSahara</strong>, ook bekend als <strong>Sahara Soul Treks</strong> en <strong>SaharaTrek</strong>, 
                  is uw partner voor authentieke <strong>Marokko woestijn treks</strong> en onvergetelijke <strong>Sahara 4x4 tours</strong>. 
                  Gevestigd in <strong>M'Hamid El Ghizlane</strong>, de poort naar de woestijn, organiseren we reizen naar de mooiste 
                  duinen van Marokko: <strong>Erg Chegaga</strong> en <strong>Erg Zahar</strong>.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Vertrek vanaf <strong>Marrakech</strong>, <strong>Ouarzazate</strong>, <strong>Zagora</strong> of rechtstreeks vanaf <strong>M'Hamid</strong>. 
                  <strong>Privé trek</strong> of in <strong>kleine groep</strong>, <strong>op maat gemaakte tour</strong>: 
                  we passen elke reis aan uw wensen aan voor totale onderdompeling in de magie van de Marokkaanse woestijn.
                </p>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
